import { AutocompleteGroupResults, Flex, Input, type AutocompleteGroupItem } from '@talos/kyoko';
import { forwardRef, useCallback, useMemo, useState, type ForwardedRef } from 'react';
import type { InnerMarketAccountSelectionListProps, MarketAccountObject, MarketAccountSelectionProps } from '../types';
import { filteredMarketObjGroups, getMarketObjAutocompleteGroupItems } from '../utils';

interface SearchListProps extends MarketAccountSelectionProps, InnerMarketAccountSelectionListProps {}

export const SearchList = forwardRef((props: SearchListProps, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    security,

    handleMarketAccountChange,
    handleExchangeCheckbox,
    selectedExchangeMarkets,
    exchangeMarketObjGroups,

    handleMarketChange,
    handleDealerCheckbox,
    selectedDealerMarkets,
    dealerMarketObjGroups,
  } = props;

  const [searchPhrase, setSearchPhrase] = useState('');
  const searchStr = useMemo(() => searchPhrase.toLowerCase(), [searchPhrase]);

  const filteredExchangeMarketObjGroups = useMemo(
    () => filteredMarketObjGroups(exchangeMarketObjGroups, searchStr),
    [exchangeMarketObjGroups, searchStr]
  );

  const filteredDealerMarketObjGroups = useMemo(
    () => filteredMarketObjGroups(dealerMarketObjGroups, searchStr),
    [dealerMarketObjGroups, searchStr]
  );

  const selectedItems = useMemo(() => {
    return [...selectedExchangeMarkets, ...selectedDealerMarkets];
  }, [selectedExchangeMarkets, selectedDealerMarkets]);

  const exchangesItems = useMemo(
    () => getMarketObjAutocompleteGroupItems(filteredExchangeMarketObjGroups),
    [filteredExchangeMarketObjGroups]
  );

  const dealersItems = useMemo(
    () => getMarketObjAutocompleteGroupItems(filteredDealerMarketObjGroups),
    [filteredDealerMarketObjGroups]
  );

  const items: AutocompleteGroupItem<any>[] = useMemo(() => {
    return [
      {
        group: 'Exchanges',
        items: exchangesItems,
      },
      {
        group: 'Dealers',
        items: dealersItems,
      },
    ];
  }, [exchangesItems, dealersItems]);

  const getLabel = useCallback((item: MarketAccountObject) => {
    return item.label;
  }, []);

  const getValue = useCallback((item: MarketAccountObject) => {
    return item.name;
  }, []);

  const handleSelectedItemChange = useCallback(
    (selectedItem: MarketAccountObject) => {
      if (selectedItem.marketAccount) {
        handleMarketAccountChange(selectedItem);
      } else {
        handleMarketChange(selectedItem);
      }
    },
    [handleMarketAccountChange, handleMarketChange]
  );

  const handleSelectedGroupChange = useCallback(
    (group: string, checked: boolean) => {
      if (group === 'Exchanges') {
        handleExchangeCheckbox(checked);
      } else {
        handleDealerCheckbox(checked);
      }
    },
    [handleExchangeCheckbox, handleDealerCheckbox]
  );

  if (security == null) {
    return null;
  }

  return (
    <Flex flexDirection="column" w="100%">
      <Input
        ref={ref}
        style={{ border: 0 }}
        placeholder="Search market..."
        onChange={e => setSearchPhrase(e.target.value)}
      />
      <AutocompleteGroupResults<MarketAccountObject>
        selectedItems={selectedItems}
        getLabel={getLabel}
        getValue={getValue}
        items={items}
        onSelectedItemChange={handleSelectedItemChange}
        onSelectedGroupChange={handleSelectedGroupChange}
      />
    </Flex>
  );
});
